import React, { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import Header from "../components/Header";
import { useAuth } from "../components/AuthProvider";
import {
  adminStats,
  adminListOrders,
  adminListCustomRequests,
  adminListDesignDetails,
  adminGetOrder,
  adminUpdateOrderStatus,
} from "../lib/api";
import { toast } from "sonner";
import { OrdersTable } from "../components/admin/OrdersTable";
import { CustomRequestsTable, DesignDetailsTable } from "../components/admin/AdminTables";
import { OrderDetailModal } from "../components/admin/OrderDetailModal";

const Admin = () => {
  const { user, loading, signOut } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [customReqs, setCustomReqs] = useState([]);
  const [details, setDetails] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const [s, o, c, d] = await Promise.all([
        adminStats(),
        adminListOrders(),
        adminListCustomRequests(),
        adminListDesignDetails(),
      ]);
      setStats(s);
      setOrders(o.orders || []);
      setCustomReqs(c.customRequests || []);
      setDetails(d.designDetails || []);
    } catch (e) {
      console.error("Failed to load admin data:", e);
      toast.error("Failed to load admin data");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.is_admin) return;
    refresh();
  }, [user, refresh]);

  const handleStatus = async (orderNumber, status) => {
    try {
      await adminUpdateOrderStatus(orderNumber, status);
      toast.success(`Order ${orderNumber} → ${status}`);
      refresh();
    } catch (e) {
      console.error("Failed to update order status:", e);
      toast.error("Failed to update status");
    }
  };

  const openOrder = async (orderNumber) => {
    try {
      const data = await adminGetOrder(orderNumber);
      setSelectedOrder(data);
    } catch (e) {
      console.error("Failed to fetch order:", e);
      toast.error("Failed to fetch order");
    }
  };

  if (loading) return <div className="min-h-screen bg-warm-ivory flex items-center justify-center"><p className="text-charcoal-ink">Loading…</p></div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!user.is_admin) return <Navigate to="/admin/login?denied=1" replace />;

  return (
    <div className="min-h-screen bg-warm-ivory">
      <Header cartItems={[]} onRemoveFromCart={() => {}} onUpdateQuantity={() => {}} />
      <section className="pt-40 md:pt-44 pb-20 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-charcoal-ink">Studio Dashboard</h1>
              <p className="text-stone-taupe mt-1">Signed in as <strong>{user.email}</strong></p>
            </div>
            <Button onClick={signOut} variant="outline" data-testid="admin-signout-button" className="border-charcoal-ink text-charcoal-ink">
              Sign out
            </Button>
          </div>

          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
              <StatCard label="Total Orders" value={stats.ordersTotal} />
              <StatCard label="Paid" value={stats.ordersPaid} />
              <StatCard label="In Design" value={stats.ordersInDesign} />
              <StatCard label="Shipped" value={stats.ordersShipped} />
              <StatCard label="Delivered" value={stats.ordersDelivered} />
              <StatCard label="Custom Requests" value={stats.customRequestsTotal} />
              <StatCard label="Design Details" value={stats.designDetailsSubmitted} />
              <StatCard label="Proofing" value={stats.ordersProofing} />
            </div>
          )}

          <Tabs defaultValue="orders" className="space-y-4">
            <TabsList>
              <TabsTrigger value="orders">Orders ({orders.length})</TabsTrigger>
              <TabsTrigger value="requests">Custom Requests ({customReqs.length})</TabsTrigger>
              <TabsTrigger value="details">Design Details ({details.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="orders">
              <OrdersTable orders={orders} busy={busy} onStatusChange={handleStatus} onView={openOrder} />
            </TabsContent>

            <TabsContent value="requests">
              <CustomRequestsTable requests={customReqs} busy={busy} />
            </TabsContent>

            <TabsContent value="details">
              <DesignDetailsTable details={details} busy={busy} />
            </TabsContent>
          </Tabs>

          {selectedOrder && (
            <OrderDetailModal data={selectedOrder} onClose={() => setSelectedOrder(null)} />
          )}
        </div>
      </section>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <Card className="border-stone-taupe/20">
    <CardContent className="p-4">
      <p className="text-xs text-stone-taupe uppercase tracking-wide">{label}</p>
      <p className="text-3xl font-bold text-charcoal-ink mt-1">{value}</p>
    </CardContent>
  </Card>
);

export default Admin;
